import { useI18n, translate as t } from "../i18n";
import { motion } from "motion/react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const HOME_SPLASH_SEEN_KEY = "ezgames.homeSplashSeen";
const PORTFOLIO_2026_SPLASH_SEEN_KEY = "ezgames.portfolio2026SplashSeen";
const PORTFOLIO_2026_ROOT = "/portfolio-2026";
const CONTROLLER_FRAME_COUNT = 101;
const CONTROLLER_FRAME_RATE = 40;
const CONTROLLER_FRAME_BUFFER = 12;

function needsControllerCanvasFallback() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isIPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return isIOS || isIPadOS;
}

function controllerFrameUrl(index: number) {
  return `/images/controller-frames/frame-${String(index + 1).padStart(3, "0")}.webp`;
}

function ControllerFrameCanvas({ onEnded }: { onEnded: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d", { alpha: true });
    if (!canvas || !context) {
      onEnded();
      return;
    }

    let cancelled = false;
    let animationFrame = 0;
    let lastDrawnFrame = -1;
    const loadedFrames = new Map<number, HTMLImageElement>();
    const pendingFrames = new Map<number, Promise<HTMLImageElement>>();

    const loadFrame = (index: number) => {
      const loaded = loadedFrames.get(index);
      if (loaded) return Promise.resolve(loaded);

      const pending = pendingFrames.get(index);
      if (pending) return pending;

      const request = new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.decoding = "async";
        image.onload = () => {
          pendingFrames.delete(index);
          if (!cancelled) loadedFrames.set(index, image);
          resolve(image);
        };
        image.onerror = () => {
          pendingFrames.delete(index);
          reject(new Error(`Unable to load controller frame ${index + 1}`));
        };
        image.src = controllerFrameUrl(index);
      });

      pendingFrames.set(index, request);
      return request;
    };

    const drawFrame = (index: number) => {
      const image = loadedFrames.get(index);
      if (!image || index === lastDrawnFrame) return;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      lastDrawnFrame = index;
    };

    const bufferFrames = (fromIndex: number) => {
      const lastIndex = Math.min(CONTROLLER_FRAME_COUNT - 1, fromIndex + CONTROLLER_FRAME_BUFFER);
      for (let index = fromIndex; index <= lastIndex; index += 1) {
        void loadFrame(index).catch(() => undefined);
      }

      for (const index of loadedFrames.keys()) {
        if (index < fromIndex - 3) loadedFrames.delete(index);
      }
    };

    const play = async () => {
      try {
        await Promise.all(
          Array.from({ length: CONTROLLER_FRAME_BUFFER }, (_, index) => loadFrame(index)),
        );
      } catch {
        if (!cancelled) onEnded();
        return;
      }

      if (cancelled) return;
      drawFrame(0);
      const startedAt = performance.now();
      const frameDuration = 1000 / CONTROLLER_FRAME_RATE;

      const tick = (now: number) => {
        if (cancelled) return;

        const index = Math.min(
          CONTROLLER_FRAME_COUNT - 1,
          Math.floor((now - startedAt) / frameDuration),
        );
        bufferFrames(index + 1);
        drawFrame(index);

        if (index >= CONTROLLER_FRAME_COUNT - 1) {
          onEnded();
          return;
        }

        animationFrame = requestAnimationFrame(tick);
      };

      animationFrame = requestAnimationFrame(tick);
    };

    void play();

    return () => {
      cancelled = true;
      cancelAnimationFrame(animationFrame);
      loadedFrames.clear();
      pendingFrames.clear();
    };
  }, [onEnded]);

  return (
    <canvas
      ref={canvasRef}
      width={720}
      height={484}
      className="h-auto w-full"
      aria-hidden="true"
    />
  );
}

function consumePortfolio2026Splash(pathname: string) {
  if (pathname !== PORTFOLIO_2026_ROOT) return false;
  if (window.sessionStorage.getItem(PORTFOLIO_2026_SPLASH_SEEN_KEY) === "1") return false;
  window.sessionStorage.setItem(PORTFOLIO_2026_SPLASH_SEEN_KEY, "1");
  return true;
}

function shouldShowOnInitialLoad(pathname: string) {
  if (pathname === PORTFOLIO_2026_ROOT) return consumePortfolio2026Splash(pathname);
  if (pathname.startsWith(`${PORTFOLIO_2026_ROOT}/`)) return false;
  if (pathname !== "/") return true;
  return window.sessionStorage.getItem(HOME_SPLASH_SEEN_KEY) === "1";
}

/** Loading state shown briefly whenever the client-side route changes. */
export default function PageLoadingScreen() {
  const { t, language } = useI18n("PageLoadingScreen");
  const location = useLocation();
  const initialLocationKey = useRef(location.key);
  const [visible, setVisible] = useState(() => shouldShowOnInitialLoad(location.pathname));
  const [useCanvasFallback, setUseCanvasFallback] = useState(needsControllerCanvasFallback);
  const hide = useCallback(() => setVisible(false), []);

  useLayoutEffect(() => {
    if (location.key === initialLocationKey.current) return;

    if (location.pathname === PORTFOLIO_2026_ROOT) {
      setVisible(consumePortfolio2026Splash(location.pathname));
      return;
    }

    if (location.pathname.startsWith(`${PORTFOLIO_2026_ROOT}/`)) {
      setVisible(false);
      return;
    }

    setVisible(true);
  }, [location.key, location.pathname]);

  return (
    <motion.div
      className="fixed inset-0 z-[250] flex flex-col items-center justify-center overflow-hidden px-6 text-white"
      style={{
        background:
          "radial-gradient(circle at 50% 35%, rgba(66,255,117,0.16), transparent 25%), radial-gradient(circle at 50% 62%, rgba(241,200,106,0.12), transparent 36%), linear-gradient(180deg, #020807 0%, #061311 48%, #030605 100%)",
        pointerEvents: visible ? "auto" : "none",
      }}
      initial={false}
      animate={{ opacity: visible ? 1 : 0 }}
      transition={{ duration: visible ? 0 : 0.28, ease: "easeOut" }}
      aria-hidden={!visible}
    >
      <div className="page-loader__glow" aria-hidden="true" />
      <motion.div
        key={`${location.key}-${useCanvasFallback ? "canvas" : "video"}`}
        className="relative z-10 h-[18rem] w-[18rem] object-contain sm:h-[26rem] sm:w-[26rem] md:h-[30rem] md:w-[30rem]"
        initial={{ opacity: 0, scale: 0.88 }}
        animate={{ opacity: visible ? 1 : 0, scale: visible ? 1 : 0.88 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        <div className="flex h-full w-full items-center justify-center">
          {useCanvasFallback ? (
            <ControllerFrameCanvas onEnded={hide} />
          ) : (
            <video
              className="h-full w-full object-contain"
              src="/images/controller.webm"
              autoPlay
              muted
              playsInline
              onEnded={hide}
              onError={() => setUseCanvasFallback(true)}
              ref={(video) => {
                if (video) video.playbackRate = 2;
              }}
            />
          )}
        </div>
      </motion.div>
      <motion.img
        src="/images/ezgames-logo.svg"
        alt={t("EZ Games")}
        width={583}
        height={233}
        className="relative z-10 mt-[-2rem] w-40 sm:w-52"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 14 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      />
    </motion.div>
  );
}
